// src/features/clinical/components/visit/VisitHistory.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { FaCalendar, FaUserMd, FaHeartbeat, FaPrescription } from 'react-icons/fa';
import { formatDate } from '../../utils/clinicalHelpers';

const VisitHistory = ({ visits }) => {
  const getStatusColor = (status) => {
    switch(status) {
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'IN_PROGRESS': return 'bg-yellow-100 text-yellow-800';
      case 'SCHEDULED': return 'bg-blue-100 text-blue-800';
      case 'CANCELLED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-medium text-gray-900">Visit History</h2>
      </div>

      <div className="divide-y divide-gray-200">
        {visits.map((visit) => (
          <div key={visit.id} className="p-6 hover:bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-3">
                <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(visit.status)}`}>
                  {visit.status}
                </span>
                <span className="text-sm font-medium text-gray-900">{visit.visitId}</span>
              </div>
              <span className="text-sm text-gray-500 flex items-center">
                <FaCalendar className="mr-1" /> {formatDate(visit.visitDate)}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-4 mt-3">
              <div className="flex items-center text-sm text-gray-600">
                <FaUserMd className="mr-2 text-blue-500" />
                Dr. {visit.doctorName}
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <FaHeartbeat className="mr-2 text-red-500" />
                {visit.department}
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <FaPrescription className="mr-2 text-green-500" />
                {visit.prescriptions?.length || 0} Prescriptions
              </div>
            </div>

            <div className="mt-3">
              <p className="text-sm text-gray-700">
                <span className="font-medium">Chief Complaint:</span> {visit.chiefComplaint}
              </p>
              {visit.diagnosis && (
                <p className="text-sm text-gray-700 mt-1">
                  <span className="font-medium">Diagnosis:</span> {visit.diagnosis}
                </p>
              )}
            </div>

            {visit.vitals && (
              <div className="mt-3 grid grid-cols-5 gap-2 bg-gray-50 p-3 rounded-lg">
                <div className="text-center">
                  <p className="text-xs text-gray-500">BP</p>
                  <p className="text-sm font-medium">{visit.vitals.bloodPressureSystolic}/{visit.vitals.bloodPressureDiastolic}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">HR</p>
                  <p className="text-sm font-medium">{visit.vitals.heartRate}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">Temp</p>
                  <p className="text-sm font-medium">{visit.vitals.temperature}°C</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">SpO₂</p>
                  <p className="text-sm font-medium">{visit.vitals.oxygenSaturation}%</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">BMI</p>
                  <p className="text-sm font-medium">{visit.vitals.bmi}</p>
                </div>
              </div>
            )}

            <div className="mt-4 flex justify-end space-x-3">
              <Link
                to={`/clinical/visits/${visit.id}`}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                View Details
              </Link>
              <Link
                to={`/clinical/prescriptions/new?visitId=${visit.id}`}
                className="text-sm text-green-600 hover:text-green-800"
              >
                Add Prescription
              </Link>
            </div>
          </div>
        ))}

        {visits.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">No visit history found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VisitHistory;